import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail, LOGO_URL } from "../lib/api";

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
      } else {
        await register(name, email, password);
      }
      toast.success(mode === "login" ? "Welcome back!" : "Account created!");
      navigate("/design");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="App">
      <Navbar />
      <div className="max-w-md mx-auto px-5 py-14">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white nb-border nb-shadow-lg rounded-3xl p-8"
        >
          <img src={LOGO_URL} alt="2T Sticker" className="h-12 mx-auto mb-6" />
          <div className="flex rounded-full nb-border overflow-hidden mb-7 font-head font-bold">
            <button
              data-testid="tab-login"
              onClick={() => { setMode("login"); setError(""); }}
              className={`flex-1 py-2.5 ${mode === "login" ? "bg-nbpink text-white" : "bg-white"}`}
            >
              Sign in
            </button>
            <button
              data-testid="tab-register"
              onClick={() => { setMode("register"); setError(""); }}
              className={`flex-1 py-2.5 border-l-[3px] border-nbblack ${mode === "register" ? "bg-nbpink text-white" : "bg-white"}`}
            >
              Register
            </button>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="font-head font-bold text-sm">Name</label>
                <input
                  data-testid="input-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full mt-1 px-4 py-3 rounded-xl nb-border bg-[#FFF9F0] focus:outline-none focus:bg-white"
                  placeholder="Your name"
                />
              </div>
            )}
            <div>
              <label className="font-head font-bold text-sm">Email</label>
              <input
                data-testid="input-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full mt-1 px-4 py-3 rounded-xl nb-border bg-[#FFF9F0] focus:outline-none focus:bg-white"
                placeholder="you@email.com"
              />
            </div>
            <div>
              <label className="font-head font-bold text-sm">Password</label>
              <input
                data-testid="input-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full mt-1 px-4 py-3 rounded-xl nb-border bg-[#FFF9F0] focus:outline-none focus:bg-white"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <p data-testid="auth-error" className="text-sm font-semibold text-red-600 bg-red-50 rounded-lg px-3 py-2 nb-border border-red-200">
                {error}
              </p>
            )}

            <button
              data-testid="auth-submit"
              disabled={busy}
              className="w-full bg-nbblack text-white font-head font-bold text-lg py-3 rounded-full nb-border nb-shadow nb-press disabled:opacity-60"
            >
              {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-[2px] bg-nbblack/15" />
            <span className="text-xs font-bold text-nbblack/40">OR</span>
            <div className="flex-1 h-[2px] bg-nbblack/15" />
          </div>

          <button
            data-testid="google-login"
            onClick={loginWithGoogle}
            className="w-full bg-white font-head font-bold py-3 rounded-full nb-border nb-shadow-sm nb-press flex items-center justify-center gap-3"
          >
            <img src="https://www.google.com/favicon.ico" alt="" className="w-5 h-5" />
            Continue with Google
          </button>

          <p className="text-center text-sm text-nbblack/50 mt-6">
            <Link to="/" className="underline hover:text-nbpink">Back to home</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
